<template>
    <div>
        <h2 class="text-h6">
            <strong>Supercharge your SMS growth and customer engagement</strong>
        </h2>

        <p class="mt-3">
            To use powerful tools like Lightboxes (pop-ups for opt-in growth) and Webchat (real-time messaging),
            complete the quick steps below:
        </p>

        <ol class="setup-steps">
            <li>
                <h2 class="text-h6 mb-2">
                    <strong>Install a small script on your website</strong>
                </h2>

                <rc-link
                    class="mt-2 mb-4"
                    href="https://redchirp.com/adding-the-redchirp-widget-to-your-website/#:~:text=Adding%20the%20RedChirp%20widget%20to%20your%20website"
                    text_transform="none"
                    inline
                >
                    How to add the RedChirp script to your website
                </rc-link>

                <p>
                    <em>
                        <strong>Note:</strong> Installing the script will not make any visible changes to your website.
                        You'll be in full control of when Lightboxes or Webchat go live. Nothing will display without
                        your approval.
                    </em>
                </p>
            </li>

            <li>
                <h2 class="text-h6 mb-3">
                    <strong>Choose Your Lightbox Template</strong>
                </h2>

                <p>Start collecting SMS opt-ins right away.</p>

                <v-alert
                    type="info"
                    color="primary"
                    class="shaded-light"
                    border="left"
                    colored-border
                >
                    The template you select is a starting point only. You'll be able to customize images, text, and
                    colors during onboarding before going live.
                </v-alert>

                <v-row>
                    <v-col
                        cols="12"
                        xl="8"
                    >
                        <v-btn
                            v-if="!selected_lightbox_template"
                            color="primary"
                            class="mt-2 mb-4"
                            outlined
                            @click="openDialog"
                        >
                            Select a Template
                        </v-btn>

                        <div v-else>
                            <m-lightbox-template-card
                                :template="selected_lightbox_template"
                                :theme="lightbox_default_theme"
                                :lightbox_params="lightbox_save_params"
                                @select="openDialog"
                            />

                            <v-btn
                                class="my-5"
                                color="primary"
                                text
                                @click="openDialog"
                            >
                                <v-icon
                                    left
                                    aria-hidden="true"
                                >
                                    mdi-lead-pencil
                                </v-icon>
                                Change Template Selection
                            </v-btn>
                        </div>
                    </v-col>
                </v-row>

                <template v-if="setup_data.lightbox_template_type">
                    <p>
                        Let us know what you want your Lightbox to say — we'll help you customize it during onboarding.
                    </p>

                    <v-row>
                        <v-col
                            cols="12"
                            md="6"
                        >
                            <rc-base-input
                                v-model="setup_data.lightbox_message"
                                type="text"
                                hint="Example: “Sign up now for 10% off your first order!”"
                                label="Lightbox Message"
                                @input="update"
                            />
                        </v-col>
                    </v-row>
                </template>
            </li>

            <li>
                <h2 class="text-h6">
                    <strong>Will you use WebChat?</strong>
                </h2>

                <v-checkbox
                    label="We plan to use the RedChirp webchat widget to let customers text us from our website"
                    v-model="setup_data.use_widget"
                    @change="doUpdate"
                />
            </li>

            <div v-if="selected_lightbox_template || setup_data.use_widget">
                <h2 class="text-body-1 mb-4">
                    <strong>Which Domains?</strong>
                </h2>

                <p class="mb-3">
                    Please enter the domain name(s) on which you would like to use the RedChirp widget(s). You can
                    add multiple using the (+) button.
                </p>

                <v-row>
                    <v-col
                        v-for="(item, index) in setup_data.urls"
                        :key="index"
                        cols="12"
                        md="4"
                        @input="update"
                    >
                        <v-text-field
                            v-model="setup_data.urls[index]"
                            append-icon="mdi-delete"
                            @click:append="onClickDeleteURL(index)"
                            @blur="onUrlBlur(index)"
                        />
                    </v-col>
                </v-row>

                <m-icon-bar
                    class="mb-3"
                    @add="onClickAddURL"
                />

                <template
                    v-if="setup_data.web_dev_first_name != null || setup_data.web_dev_last_name != null || setup_data.web_dev_email_address != null"
                >
                    <v-row>
                        <v-col>
                            <v-text-field
                                label="Web Developer First Name"
                                v-model="setup_data.web_dev_first_name"
                                autocomplete="off"
                                @input="update"
                            />
                        </v-col>
                        <v-col>
                            <v-text-field
                                label="Web Developer Last Name"
                                v-model="setup_data.web_dev_last_name"
                                autocomplete="off"
                                @input="update"
                            />
                        </v-col>
                    </v-row>

                    <v-text-field
                        label="Web Developer Email Address"
                        v-model="setup_data.web_dev_email_address"
                        autocomplete="off"
                        @input="update"
                    />
                </template>
            </div>
        </ol>

        <v-switch
            label="This section is complete"
            v-model="setup_data.complete"
            @change="doUpdate"
        />

        <v-dialog
            v-model="lightbox_dialog"
            max-width="1600px"
            scrollable
            persistent
        >
            <v-card>
                <v-card-title class="d-flex flex-column align-start my-2">
                    <h2 class="mb-0">
                        Select a lightbox template
                    </h2>
                </v-card-title>

                <v-card-text>
                    <lightbox-template-selection
                        ref="template_selection"
                        is_simple
                        :lightbox_params="lightbox_save_params"
                        :theme="lightbox_default_theme"
                        @template-selected="onTemplateSelected"
                        @template-deselected="deselectTemplate"
                    />
                </v-card-text>

                <v-card-actions>
                    <v-spacer />

                    <v-btn
                        text
                        class="mr-1"
                        @click="onTemplateSelectionCancel"
                    >
                        Cancel
                    </v-btn>

                    <v-btn
                        color="primary"
                        @click="onTemplateSelectionOk"
                    >
                        Ok
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import _ from 'lodash';
import LightboxTemplates from '@/mixins/lightbox/lightbox-templates.js';

export default {
    mixins: [LightboxTemplates],

    components: {
        LightboxTemplateSelection: () => import('@/views/lightbox/lightbox-template-selection.vue'),
        mLightboxTemplateCard: () => import('@/components/lightbox/m-lightbox-template-card.vue'),
    },

    props: {
        profile: {
            type: Object,
            default: function () { return {}; },
        },
    },

    data() {
        return {
            setup_data: {},
            lightbox_dialog: false,
            lightbox_save_params: null,
            temp_selected_lightbox_template_type: null, // for transient selection in dialog
            lightbox_default_theme: {
                font_family: window.sessionStorage.getItem('preferred_lightbox_font_family') || 'Arial, sans-serif',
                colors: {
                    primary: {
                        label: 'Primary Color',
                        value: window.sessionStorage.getItem('preferred_lightbox_template_color') || '#007bff',
                    },
                },
                header: {
                    type: 'text',
                    text: '',
                    image: null
                }
            }
        }
    },

    computed: {
        selected_lightbox_template() {
            if (this.setup_data.lightbox_template_type) {
                return this.mixin_base_templates.find(t => t.id === this.setup_data.lightbox_template_type);
            } else {
                return null;
            }
        },

        dialog_selected_template() {
            if (this.temp_selected_lightbox_template_type) {
                return this.mixin_base_templates.find(t => t.id === this.temp_selected_lightbox_template_type);
            }
            return null;
        },

        has_unsaved_template_changes() {
            return this.temp_selected_lightbox_template_type !== this.setup_data.lightbox_template_type;
        },
    },

    mounted() {
        this.setup_data = _.cloneDeep(this.profile.website_organization_setup_data);

        if (this.setup_data.urls.length == 0) {
            this.setup_data.urls.push("");
        }
    },

    methods: {
        initializeDialog() {
            this.temp_selected_lightbox_template_type = this.setup_data.lightbox_template_type || null;
            this.$nextTick(this.syncDialogSelection);
        },

        syncDialogSelection() {
            if (!this.$refs.template_selection) return;

            if (this.temp_selected_lightbox_template_type && this.dialog_selected_template) {
                this.$refs.template_selection.onTemplateSelected(this.dialog_selected_template);
            } else {
                this.$refs.template_selection.deselectTemplate();
            }
        },

        openDialog() {
            this.lightbox_dialog = true;
            this.initializeDialog();
        },

        closeDialog() {
            this.lightbox_dialog = false;
            this.resetTransientState();
        },

        resetTransientState() {
            this.temp_selected_lightbox_template_type = this.setup_data.lightbox_template_type || null;
        },

        onTemplateSelected(template) {
            this.temp_selected_lightbox_template_type = template.id;
        },

        deselectTemplate() {
            this.temp_selected_lightbox_template_type = null;
        },

        onTemplateSelectionOk() {
            if (this.has_unsaved_template_changes) {
                this.setup_data = {
                    ...this.setup_data,
                    lightbox_template_type: this.temp_selected_lightbox_template_type,
                };
                this.doUpdate();
            }
            this.closeDialog();
        },

        onTemplateSelectionCancel() {
            this.closeDialog();
        },

        update: _.debounce(function () {
            this.doUpdate();
        }, 751),

        doUpdate() {
            this.$rc.rest({
                spinner: false,
                endpoint: 'PUT: /api/v1/welcome/website-setup-data',

                params: this.setup_data,

                on_success: (data) => {

                    this.$emit('reload');

                },

                on_error: 'Unable to save form data',
            });
        },

        onClickAddURL() {
            this.setup_data.urls.push("");
        },

        onClickDeleteURL(index) {
            this.setup_data.urls.splice(index, 1);
            this.doUpdate();
        },

        onUrlBlur(index) {
            const val = this.setup_data.urls[index];
            this.setup_data.urls[index] = this.$rc.maskUrl(val);
            this.setup_data.urls = [...this.setup_data.urls]; // trigger reactivity
            this.doUpdate();
        },
    },

    watch: {
        'setup_data.lightbox_template_type'(newVal) {
            if (!this.lightbox_dialog) {
                this.temp_selected_lightbox_template_type = newVal || null;
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.setup-steps {
    list-style: none;
    counter-reset: step-counter;
    padding-left: 0;

    li {
        counter-increment: step-counter;
        margin-bottom: 1rem;
        position: relative;
        padding-left: 0;

        h2 {
            position: relative;

            &::before {
                content: counter(step-counter) ".";
                font-weight: bold;
                margin-right: 0.5rem;
            }
        }
    }
}
</style>
